import { PRODUCT_TYPES, type ProductType } from '@/types/investor';
import {
  CONTACT_TYPE_GROUPS,
  type EncompassContactType,
  type EncompassTemplateContact,
  type InvestorEncompassTemplate
} from '@/types/investor-encompass';

export interface MergedTemplateContactEntry {
  templateId: string;
  templateName: string;
  products: ProductType[];
  contact: EncompassTemplateContact | null;
  hasData: boolean;
}

export interface MergedTemplateContactTile {
  contactType: EncompassContactType;
  groupKey: string;
  entries: MergedTemplateContactEntry[];
  filledCount: number;
  totalCount: number;
  status: 'shared' | 'varies' | 'missing';
  sharedContact: EncompassTemplateContact | null;
}

export interface MergedTemplateContactGroup {
  groupKey: string;
  tiles: MergedTemplateContactTile[];
}

type ContactMergeField = keyof Pick<
  EncompassTemplateContact,
  | 'investorName'
  | 'contactName'
  | 'department'
  | 'attentionTo'
  | 'addressStreet1'
  | 'addressStreet2'
  | 'city'
  | 'state'
  | 'zip'
  | 'phone'
  | 'fax'
  | 'email'
  | 'website'
  | 'notes'
>;

const CONTACT_MERGE_FIELDS: ContactMergeField[] = [
  'investorName',
  'contactName',
  'department',
  'attentionTo',
  'addressStreet1',
  'addressStreet2',
  'city',
  'state',
  'zip',
  'phone',
  'fax',
  'email',
  'website',
  'notes'
];

export const APPENDIX_B_CONTACT_TYPES: Array<{ groupKey: string; contactType: EncompassContactType }> =
  Object.entries(CONTACT_TYPE_GROUPS).flatMap(([groupKey, contactTypes]) =>
    contactTypes.map((contactType) => ({ groupKey, contactType }))
  );

function normalizeValue(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function contactSignature(contact: EncompassTemplateContact | null): string {
  return CONTACT_MERGE_FIELDS.map((field) => normalizeValue(contact?.[field])).join('\u001f');
}

export function hasMergedContactData(contact: EncompassTemplateContact | null | undefined): boolean {
  if (!contact) {
    return false;
  }

  return CONTACT_MERGE_FIELDS.some((field) => Boolean(normalizeValue(contact[field])));
}

function getTemplateProducts(template: InvestorEncompassTemplate): ProductType[] {
  const products = template.assignments && template.assignments.length > 0
    ? template.assignments.map((assignment) => assignment.product)
    : template.applicableProducts ?? [];
  const selected = new Set(products);

  return PRODUCT_TYPES.filter((product) => selected.has(product));
}

function getContactByType(
  template: InvestorEncompassTemplate,
  contactType: EncompassContactType
): EncompassTemplateContact | null {
  const contacts = template.contacts?.filter((contact) => contact.contactType === contactType) ?? [];
  return contacts.find(hasMergedContactData) ?? contacts[0] ?? null;
}

export function buildMergedTemplateContactGroups(
  templates: InvestorEncompassTemplate[]
): MergedTemplateContactGroup[] {
  const tiles = APPENDIX_B_CONTACT_TYPES.map(({ groupKey, contactType }) => {
    const entries = templates.map((template) => {
      const contact = getContactByType(template, contactType);
      return {
        templateId: template.id,
        templateName: template.templateName,
        products: getTemplateProducts(template),
        contact,
        hasData: hasMergedContactData(contact)
      };
    });

    const filledEntries = entries.filter((entry) => entry.hasData);
    const filledCount = filledEntries.length;
    const totalCount = templates.length;
    const signatures = new Set(entries.map((entry) => contactSignature(entry.contact)));
    const status = filledCount === 0
      ? 'missing'
      : filledCount === totalCount && signatures.size === 1
        ? 'shared'
        : 'varies';

    return {
      contactType,
      groupKey,
      entries,
      filledCount,
      totalCount,
      status,
      sharedContact: status === 'shared' ? filledEntries[0]?.contact ?? null : null
    } satisfies MergedTemplateContactTile;
  });

  return Object.keys(CONTACT_TYPE_GROUPS).map((groupKey) => ({
    groupKey,
    tiles: tiles.filter((tile) => tile.groupKey === groupKey)
  }));
}

export function formatMergedContactAddress(contact: EncompassTemplateContact): string[] {
  const lines = [
    contact.addressStreet1?.trim(),
    contact.addressStreet2?.trim(),
    [contact.city, contact.state, contact.zip].filter(Boolean).join(', ').trim()
  ].filter((line): line is string => Boolean(line));

  return lines;
}
